import React, { useMemo } from 'react';
import './quantity-spin-input.scss';
import { useField, useFormikContext } from 'formik';

export type QuantitySpinInputProps = {
  name: string;
  min?: number;
  max?: number;
  className?: string;
  automationid: string;
  disabled?: boolean;
  label?: string;
  lightMode?: boolean;
  /**
   *  Optional function will be called when control value changes.
   *
   *  @param value - the new control value
   *  @returns false or undefined if the new control value should be
   *  discarded.
   *  @returns true otherwise.
   *
   * @param value
   */
  onModified?: (value: number) => boolean | undefined;
};

export function QuantitySpinInput({
  label,
  automationid,
  min,
  onModified,
  max,
  disabled = false,
  lightMode = false,
  ...props
}: QuantitySpinInputProps) {
  const [field] = useField(props);
  const { setFieldValue } = useFormikContext();

  const normalizeValue = (value) => {
    let newValue = value ? parseInt(value) : 0;

    if ((min || min === 0) && min > newValue) {
      newValue = min;
    }

    if ((max || max === 0) && max < newValue) {
      newValue = max;
    }
    return newValue;
  };

  const valueMemo = useMemo(() => normalizeValue(field.value), [field?.value]);

  const setValue = (value) => {
    if (!onModified || onModified(normalizeValue(value))) {
      setFieldValue(field.name, normalizeValue(value));
    }
  };

  const increaseValue = async () => {
    if (max === undefined || valueMemo < max) {
      setValue(valueMemo + 1);
    }
  };

  const decreaseValue = async () => {
    if (min === undefined || valueMemo > min) {
      setValue(valueMemo - 1);
    }
  };

  const isValueEqualToMax = max !== undefined ? valueMemo === max : false;
  const isValueEqualToMin = min !== undefined ? valueMemo === min : false;

  return (
    <div className={`quantity-spin-input ${props?.className ? props.className : ''}`}>
      {label && <span className="input-section">{label}</span>}
      <div className="input-section">
        <span
          onClick={!disabled ? decreaseValue : undefined}
          className={`icon-minus-utility reskin ${lightMode ? 'dark' : ''} ${
            disabled || isValueEqualToMin ? 'invisible' : ''
          }`}
          automationid={`${automationid}_Minus`}
        />
        <div className="number-input-container">
          <input
            {...field}
            type="number"
            onChange={(event) => {
              if (disabled) {
                return;
              }
              event.preventDefault();
              const valueNew = event.target.value;

              const regex = /^$|^[0-9\b]+$/;
              if (!valueNew || regex.test(valueNew.toString())) {
                setValue(valueNew);
              }
            }}
            onBlur={() => {
              if (disabled) {
                return;
              }
              field.onBlur;
            }}
            name={field.name}
            maxLength={2}
            className={`input number-input ${lightMode ? 'dark' : ''} ${
              disabled ? 'disabled' : ''
            }`}
            value={valueMemo}
            automationid={`${automationid}_Value`}
            disabled={disabled}
          />
        </div>
        <span
          onClick={!disabled ? increaseValue : undefined}
          automationid={`${automationid}_Plus`}
          className={`icon-add-utility reskin ${lightMode ? 'dark' : ''} ${
            disabled || isValueEqualToMax ? 'invisible' : ''
          }`}
        />
      </div>
    </div>
  );
}
